/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { bucketService } from './api';

// Upload configuration based on internet speed
export interface UploadConfig {
  chunkSize: number;
  concurrency: number;
  speedTier: 'slow' | 'medium' | 'fast' | 'very-fast';
}

// Speed tiers and their configurations
const UPLOAD_CONFIGS: Record<string, UploadConfig> = {
  'slow': { chunkSize: 5 * 1024 * 1024, concurrency: 2, speedTier: 'slow' },
  'medium': { chunkSize: 25 * 1024 * 1024, concurrency: 4, speedTier: 'medium' },
  'fast': { chunkSize: 50 * 1024 * 1024, concurrency: 6, speedTier: 'fast' },
  'very-fast': { chunkSize: 100 * 1024 * 1024, concurrency: 10, speedTier: 'very-fast' },
};

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 30000; // 30 seconds

export type UploadStatus = 'pending' | 'uploading' | 'completed' | 'failed' | 'paused';

export interface UploadTask {
  id: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: UploadStatus;
  error?: string;
  bucketId: string;
  projectId: string;
  path: string;
  // Internal tracking
  uploadId?: string;
  completedParts: { part_number: number; etag: string }[];
  totalChunks: number;
  file: File;
}

interface ChunkTask {
  partNumber: number;
  start: number;
  end: number;
  retries: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  progress: number;
}

type UploadEventType = 'progress' | 'complete' | 'error' | 'statusChange';
type UploadEventCallback = (task: UploadTask) => void;

class UploadManager {
  private tasks: Map<string, UploadTask> = new Map();
  private chunkTasks: Map<string, ChunkTask[]> = new Map();
  private activeUploads: Map<string, AbortController> = new Map();
  private listeners: Map<UploadEventType, Set<UploadEventCallback>> = new Map();
  private config: UploadConfig = UPLOAD_CONFIGS['fast'];
  private isInitialized = false;

  constructor() {
    // Initialize speed detection
    this.detectSpeed();
  }

  private async detectSpeed(): Promise<void> {
    if (this.isInitialized) return;
    this.isInitialized = true;

    try {
      let speedMbps = 50; // Default to fast

      if ('connection' in navigator) {
        const conn = (navigator as any).connection;
        if (conn && conn.downlink) {
          speedMbps = conn.downlink;
        }
      }

      if (speedMbps < 10) {
        this.config = UPLOAD_CONFIGS['slow'];
      } else if (speedMbps < 50) {
        this.config = UPLOAD_CONFIGS['medium'];
      } else if (speedMbps < 100) {
        this.config = UPLOAD_CONFIGS['fast'];
      } else {
        this.config = UPLOAD_CONFIGS['very-fast'];
      }

      console.log(`UploadManager: ${this.config.speedTier} (${speedMbps.toFixed(1)} Mbps) - ${this.config.chunkSize / (1024 * 1024)}MB chunks, ${this.config.concurrency} parallel`);
    } catch {
      // Use default config
    }
  }

  getConfig(): UploadConfig {
    return this.config;
  }

  // Event system for UI updates
  on(event: UploadEventType, callback: UploadEventCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
    return () => this.listeners.get(event)?.delete(callback);
  }

  private emit(event: UploadEventType, task: UploadTask): void {
    this.listeners.get(event)?.forEach(cb => cb(task));
  }

  getTasks(): UploadTask[] {
    return Array.from(this.tasks.values());
  }

  getTask(id: string): UploadTask | undefined {
    return this.tasks.get(id);
  }

  // Add a new upload task
  async addUpload(
    file: File,
    bucketId: string,
    projectId: string,
    path: string
  ): Promise<string> {
    const taskId = `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const task: UploadTask = {
      id: taskId,
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: 'pending',
      bucketId,
      projectId,
      path,
      completedParts: [],
      totalChunks: 0,
      file,
    };

    this.tasks.set(taskId, task);
    this.emit('statusChange', task);

    // Start upload
    this.startUpload(taskId);

    return taskId;
  }

  // Add multiple uploads
  async addUploads(
    files: { file: File; path: string }[],
    bucketId: string,
    projectId: string
  ): Promise<string[]> {
    const taskIds: string[] = [];
    for (const { file, path } of files) {
      const taskId = await this.addUpload(file, bucketId, projectId, path);
      taskIds.push(taskId);
    }
    return taskIds;
  }

  private async startUpload(taskId: string): Promise<void> {
    const task = this.tasks.get(taskId);
    if (!task) return;

    task.status = 'uploading';
    this.emit('statusChange', task);

    try {
      const { chunkSize, concurrency } = this.config;
      const useChunkedUpload = task.fileSize > 10 * 1024 * 1024; // 10MB threshold

      if (useChunkedUpload) {
        await this.uploadChunked(taskId, chunkSize, concurrency);
      } else {
        await this.uploadSimple(taskId);
      }

      task.status = 'completed';
      task.progress = 100;
      this.emit('complete', task);
      this.emit('statusChange', task);

      // Sync bucket stats
      await bucketService.sync(task.bucketId, task.projectId);
    } catch (error: any) {
      task.status = 'failed';
      task.error = error?.message || 'Upload failed';
      this.emit('error', task);
      this.emit('statusChange', task);
    }
  }

  private async uploadSimple(taskId: string): Promise<void> {
    const task = this.tasks.get(taskId);
    if (!task) throw new Error('Task not found');

    await bucketService.uploadObjectWithProgress(
      task.bucketId,
      task.projectId,
      task.file,
      task.path,
      (progress) => {
        task.progress = progress;
        this.emit('progress', task);
      }
    );
  }

  private async uploadChunked(
    taskId: string,
    chunkSize: number,
    concurrency: number
  ): Promise<void> {
    const task = this.tasks.get(taskId);
    if (!task) throw new Error('Task not found');

    // 1. Initiate multipart upload
    const initResponse = await bucketService.initiateMultipartUpload(
      task.bucketId,
      task.projectId,
      task.path,
      task.file.type || 'application/octet-stream',
      task.fileSize
    );

    task.uploadId = initResponse.data.upload_id;
    const effectiveChunkSize = Math.min(chunkSize, initResponse.data.chunk_size || chunkSize);
    task.totalChunks = Math.ceil(task.fileSize / effectiveChunkSize);

    // 2. Prepare chunk tasks
    const chunks: ChunkTask[] = [];
    for (let i = 0; i < task.totalChunks; i++) {
      chunks.push({
        partNumber: i + 1,
        start: i * effectiveChunkSize,
        end: Math.min((i + 1) * effectiveChunkSize, task.fileSize),
        retries: 0,
        status: 'pending',
        progress: 0,
      });
    }
    this.chunkTasks.set(taskId, chunks);

    // 3. Upload chunks with retry logic
    await this.uploadChunksWithRetry(taskId, concurrency);

    // 4. Complete multipart upload
    const allChunks = this.chunkTasks.get(taskId)!;
    if (allChunks.some(c => c.status !== 'completed')) {
      throw new Error('Some chunks failed to upload');
    }

    await bucketService.completeMultipartUpload(
      task.bucketId,
      task.projectId,
      task.uploadId!,
      task.path,
      task.completedParts.sort((a, b) => a.part_number - b.part_number)
    );

    // Cleanup
    this.chunkTasks.delete(taskId);
  }

  private async uploadChunksWithRetry(
    taskId: string,
    concurrency: number
  ): Promise<void> {
    const task = this.tasks.get(taskId);
    const chunks = this.chunkTasks.get(taskId);
    if (!task || !chunks) throw new Error('Task not found');

    let chunkIndex = 0;

    const uploadNextChunk = async (): Promise<void> => {
      while (chunkIndex < chunks.length) {
        const currentIdx = chunkIndex++;
        const chunk = chunks[currentIdx];

        if (chunk.status === 'completed') continue;

        chunk.status = 'uploading';

        let success = false;
        let lastError: Error | null = null;

        // Retry loop
        while (chunk.retries <= MAX_RETRIES && !success) {
          try {
            const blob = task.file.slice(chunk.start, chunk.end);

            const response = await bucketService.uploadPart(
              task.bucketId,
              task.projectId,
              task.uploadId!,
              chunk.partNumber,
              blob,
              task.path,
              (progress) => {
                chunk.progress = progress;
                this.updateOverallProgress(taskId);
              }
            );

            // Success
            chunk.status = 'completed';
            chunk.progress = 100;
            task.completedParts.push({
              part_number: response.data.part_number,
              etag: response.data.etag,
            });
            success = true;
          } catch (error: any) {
            lastError = error;
            chunk.retries++;

            if (chunk.retries <= MAX_RETRIES) {
              // Exponential backoff
              const delay = Math.min(
                INITIAL_RETRY_DELAY * Math.pow(2, chunk.retries - 1),
                MAX_RETRY_DELAY
              );
              console.log(`Chunk ${chunk.partNumber} failed, retrying in ${delay}ms (attempt ${chunk.retries}/${MAX_RETRIES})`);
              await this.sleep(delay);
            }
          }
        }

        if (!success) {
          chunk.status = 'failed';
          throw lastError || new Error(`Chunk ${chunk.partNumber} failed after ${MAX_RETRIES} retries`);
        }
      }
    };

    // Start concurrent workers
    const workers = Array(Math.min(concurrency, chunks.length))
      .fill(null)
      .map(() => uploadNextChunk());

    await Promise.all(workers);
  }

  private updateOverallProgress(taskId: string): void {
    const task = this.tasks.get(taskId);
    const chunks = this.chunkTasks.get(taskId);
    if (!task || !chunks) return;

    const totalProgress = chunks.reduce((sum, c) => sum + c.progress, 0);
    task.progress = Math.round(totalProgress / chunks.length);
    this.emit('progress', task);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Cancel an upload
  cancelUpload(taskId: string): void {
    const task = this.tasks.get(taskId);
    if (!task) return;

    // Abort any active upload
    const controller = this.activeUploads.get(taskId);
    if (controller) {
      controller.abort();
      this.activeUploads.delete(taskId);
    }

    // If we have an uploadId, abort the multipart upload
    if (task.uploadId) {
      bucketService.abortMultipartUpload(
        task.bucketId,
        task.projectId,
        task.uploadId,
        task.path
      ).catch(() => {});
    }

    task.status = 'failed';
    task.error = 'Cancelled by user';
    this.emit('statusChange', task);
  }

  // Retry a failed upload
  async retryUpload(taskId: string): Promise<void> {
    const task = this.tasks.get(taskId);
    if (!task || task.status !== 'failed') return;

    // Reset task state
    task.status = 'pending';
    task.progress = 0;
    task.error = undefined;
    task.uploadId = undefined;
    task.completedParts = [];

    this.emit('statusChange', task);
    await this.startUpload(taskId);
  }

  // Remove a task from the list
  removeTask(taskId: string): void {
    this.tasks.delete(taskId);
    this.chunkTasks.delete(taskId);
    this.activeUploads.delete(taskId);
  }

  // Clear all completed/failed tasks
  clearCompleted(): void {
    const idsToDelete: string[] = [];
    this.tasks.forEach((task, id) => {
      if (task.status === 'completed' || task.status === 'failed') {
        idsToDelete.push(id);
      }
    });
    idsToDelete.forEach(id => {
      this.tasks.delete(id);
      this.chunkTasks.delete(id);
    });
  }

  // Check if there are any active uploads
  hasActiveUploads(): boolean {
    return Array.from(this.tasks.values()).some(
      t => t.status === 'uploading' || t.status === 'pending'
    );
  }
}

// Singleton instance - persists across page navigation
export const uploadManager = new UploadManager();

// Warn user before leaving if uploads are in progress
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (uploadManager.hasActiveUploads()) {
      e.preventDefault();
      e.returnValue = 'You have uploads in progress. Are you sure you want to leave?';
      return e.returnValue;
    }
  });
}
